import { Button } from '@helvetia/components/Button'
import Input from '@helvetia/components/forms/Input'
import { Option } from '@helvetia/components/forms/NativeSelect/NativeSelect.types'
import PhoneInput from '@helvetia/components/forms/PhoneInput'
import { Col, Row } from '@helvetia/components/layout'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import { Field, Form, Formik } from 'formik'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'

import AgentService from '../../../core/services/agent.service'
import { LifecycleView } from '../../_components/ConnectComponent'
import { DropDownSelect } from '../../_components/DropDownSelect'
import { AdobeEventManager } from '../../_utils/adobeAnalytics'
import { SignUpPresenter } from './SignUp.presenter'
import { validateFieldForm } from './SignUp.validators'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

export interface AgentBrokerDataForm {
  firstName: string
  lastName: string
  email: string
  phone: string
  agentBrokerNumber: string
  agencyId?: number
}

interface Props {
  presenter: SignUpPresenter
  onSubmit: (values: AgentBrokerDataForm) => void
}

interface State {
  initialValues?: AgentBrokerDataForm
  agencyList: Option[]
  isLoading: boolean
}

export class SignUpRaw
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: SignUpPresenter

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.state = {
      initialValues: null,
      agencyList: [],
      isLoading: false,
    }
  }

  setInitialValues = (initialValues, agencyList) => {
    this.setState({
      initialValues,
      agencyList,
    })
  }

  addDefaultOption = (list: Option[]) => {
    let defaultOption = [
      {
        text: this.props.t('contactData.agencyNumber.default'),
        value: '0',
      } as Option,
    ]
    list = defaultOption.concat(list)

    return list
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  handleClickContinuar = (values: AgentBrokerDataForm) => this.presenter.onContinue(values)

  render() {
    const { t } = this.props
    const { initialValues, agencyList } = this.state

    if (
      !this.state.initialValues ||
      (AgentService.isAgent() && agencyList.length < 1)
    )
      return <Spinner size="xl" />

    return (
      <>
        <Row>
          <Col tablet={ 12 } desktop={ 12 } mobile={ 12 }>
            <Row marginTop="xs" marginBottom="s">
              <Text as="div" color="black" size="mBold">
                { t('signUp.welcome') }
              </Text>
            </Row>
            <Row marginBottom="m">
              <Text as="div" color="black" size="m">
                { t('signUp.p1') }
              </Text>
              <Text as="div" color="black" size="m">
                { t('signUp.p2') }
              </Text>
            </Row>

            <Formik
              initialValues={ initialValues }
              onSubmit={ this.handleClickContinuar }
              validate={ values => validateFieldForm(values) }
              render={ ({ values }) => (
                <Form>
                  <Row>
                    <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={ Input }
                            name="firstName"
                            text={ t('contactData.firstName.question') }
                            label={ t('contactData.firstName.label') }
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={ Input }
                            name="lastName"
                            text={ t('contactData.lastName.question') }
                            label={ t('contactData.lastName.label') }
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                      <Row marginBottom="s">
                        <Col>
                          <Field
                            component={ Input }
                            name="email"
                            text={ t('contactData.email.question') }
                            label={ t('contactData.email.label') }
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                    <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                      <Row marginBottom="s">
                        <Col mobile={ 12 }>
                          <Field
                            component={ PhoneInput }
                            name="phone"
                            text={ t('contactData.phone.question') }
                            label={ t('contactData.phone.label') }
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  { AgentService.isAgent() && (
                    <Row>
                      <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                        <Row marginBottom="s">
                          <Col mobile={ 12 }>
                            <Field
                              component={ Input }
                              name="agentBrokerNumber"
                              text={ t('contactData.agentNumber.question') }
                              label={ t('contactData.agentNumber.label') }
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                        <Row marginBottom="s">
                          <Col>
                            <Field
                              component={ DropDownSelect }
                              name="agencyId"
                              question={ t('contactData.agencyNumber.question') }
                              options={ agencyList }
                              defaultSelected={ values.agencyId }
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  ) }
                  { AgentService.isBroker() && (
                    <Row>
                      <Col mobile={ 12 } tablet={ 6 } desktop={ 6 }>
                        <Row marginBottom="s">
                          <Col mobile={ 12 }>
                            <Field
                              component={ Input }
                              name="agentBrokerNumber"
                              text={ t('contactData.brokerNumber.question') }
                              label={ t('contactData.brokerNumber.label') }
                              required
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  ) }

                  <Row verticalGutter marginTop="xs" marginBottom="m">
                    <Col verticalGutter mobile={ 12 } tablet={ 3 } desktop={ 3 }>
                      <Button primary type="submit">
                        { t('buttons.save') }
                      </Button>
                    </Col>
                  </Row>
                </Form>
              ) }
            />
          </Col>
        </Row>
      </>
    )
  }
}

export const SignUpView = withNamespaces()(withRouter(SignUpRaw))
